/**
 * <CategorySection />
 *
 * One forum-index block.
 *
 * parent WITH children  → non-clickable red header + child rows (unchanged).
 * parent WITHOUT children → single clickable card: red header + one Link row
 *   navigating to /forums/c/:slug so its threads are reachable.
 *
 * Tap targets meet the 48 px design-system minimum.
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

interface ChildNode {
  id: string;
  name: string;
  slug: string;
  thread_count?: number;
}

interface ParentNode {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  thread_count?: number;
  children: ChildNode[];
}

export function CategorySection({ parent }: { parent: ParentNode }) {
  return (
    <section
      data-testid={`forum-section-${parent.slug}`}
      className="rounded-2xl overflow-hidden border border-surface-border shadow-card bg-white"
    >
      {/* Red header bar -- not clickable per Carlton's spec. */}
      <header className="bg-brand text-white px-5 py-3">
        <h2 className="font-display text-lg md:text-xl font-black tracking-tight">
          {parent.name}
        </h2>
        {parent.description && (
          <p className="text-white/80 text-sm mt-0.5 line-clamp-1">
            {parent.description}
          </p>
        )}
      </header>

      {parent.children.length === 0 ? (
        /* Childless parent: render one clickable row so its threads are reachable. */
        <Link
          to={`/forums/c/${parent.slug}`}
          data-testid={`forum-child-${parent.slug}`}
          className="flex items-center justify-between gap-3 min-h-12 px-5 py-3 text-ink hover:bg-surface-sunken transition-colors btn-target"
        >
          <span className="font-semibold truncate">
            {parent.description || "Browse threads"}
          </span>
          <span className="inline-flex items-center gap-2 text-ink-muted text-sm">
            {typeof parent.thread_count === "number" && (
              <span className="font-mono">
                {parent.thread_count} thread{parent.thread_count === 1 ? "" : "s"}
              </span>
            )}
            <ChevronRight className="h-5 w-5" aria-hidden />
          </span>
        </Link>
      ) : (
        <ul className="divide-y divide-surface-border">
          {parent.children.map((child) => (
            <li key={child.id}>
              <Link
                to={`/forums/c/${child.slug}`}
                data-testid={`forum-child-${child.slug}`}
                className="flex items-center justify-between gap-3 min-h-12 px-5 py-3 text-ink hover:bg-surface-sunken transition-colors btn-target"
              >
                <span className="font-semibold truncate">{child.name}</span>
                <span className="inline-flex items-center gap-2 text-ink-muted text-sm">
                  {typeof child.thread_count === "number" && (
                    <span className="font-mono">
                      {child.thread_count} thread{child.thread_count === 1 ? "" : "s"}
                    </span>
                  )}
                  <ChevronRight className="h-5 w-5" aria-hidden />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
